import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../hooks/useTheme'
import { useFontSize } from '../../hooks/useFontSize'
import { useAuth } from '../../hooks/useAuth'
import CityPickerDropdown from './CityPickerDropdown'
import LanguagePickerDropdown from './LanguagePickerDropdown'
import styles from './MenuBar.module.css'

interface MenuBarProps {
  lang: string
  city: string
}

export default function MenuBar({ lang, city }: MenuBarProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [theme, applyTheme] = useTheme()
  const [fontSize, applyFontSize] = useFontSize()
  const { user, isLoading, signIn, register, signOut, updateUserPreferences, isRefreshing } = useAuth()

  const themeLabel =
    theme === 'light' ? t('toggle_theme_dark')
    : theme === 'dark'  ? t('toggle_theme_paper')
    :                     t('toggle_theme_light')

  const homeHref = city ? `/${lang}/${city}/` : '/'

  // Toggle theme locally and, when signed in, persist it to Django so it
  // survives logout/login cycles.
  function handleThemeToggle() {
    const nextTheme = theme === 'light' ? 'dark' : theme === 'dark' ? 'paper' : 'light'
    applyTheme(nextTheme)
    if (user) {
      void updateUserPreferences({ theme: nextTheme }).catch(() => {
        // Non-fatal — preference is still saved locally in localStorage.
      })
    }
  }

  function handleFontSizeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value as 'small' | 'medium' | 'large'
    applyFontSize(next)
    if (user) {
      void updateUserPreferences({ font_size: next }).catch(() => {
        // Non-fatal — preference is still saved locally in localStorage.
      })
    }
  }

  function handleCitySelect(newCitySlug: string) {
    navigate(`/${lang}/${newCitySlug}/`)
    try {
      localStorage.setItem(
        'radiri-last-selection',
        JSON.stringify({ lang, citySlug: newCitySlug }),
      )
    } catch { /* non-fatal */ }
    if (user) {
      void updateUserPreferences({ city_slug: newCitySlug, news_language: lang }).catch(() => {})
    }
  }

  function handleLangSelect(newLang: string) {
    navigate(`/${newLang}/${city}/`)
    try {
      localStorage.setItem(
        'radiri-last-selection',
        JSON.stringify({ lang: newLang, citySlug: city }),
      )
    } catch { /* non-fatal */ }
    if (user) {
      void updateUserPreferences({ city_slug: city, news_language: newLang }).catch(() => {})
    }
  }

  return (
    <header className={styles.menuBar} role="banner">
      <nav className={styles.inner} aria-label={t('app_name')}>
        <a href={homeHref} className={styles.logo}>
          {t('app_name')}
        </a>

        {isRefreshing && (
          <span className={styles.refreshingStatus} role="status" aria-live="polite">
            <span className={styles.refreshingDot} aria-hidden="true" />
            {t('token_refreshing')}
          </span>
        )}

        <div className={styles.actions}>
          {city && (
            <>
              <CityPickerDropdown
                currentCity={city}
                currentLang={lang}
                onSelect={handleCitySelect}
              />
              <LanguagePickerDropdown
                currentLang={lang}
                currentCity={city}
                onSelect={handleLangSelect}
              />
            </>
          )}
          <Link to={`/faq/${lang}`} className={styles.faqLink}>
            {t('faq')}
          </Link>
          <button
            type="button"
            className={styles.themeBtn}
            onClick={handleThemeToggle}
            aria-label={themeLabel}
            title={themeLabel}
          >
            {theme === 'light' ? '🌙' : theme === 'dark' ? '📰' : '☀️'}
          </button>
          <select
            className={styles.fontSizeSelect}
            value={fontSize}
            onChange={handleFontSizeChange}
            aria-label={t('font_size')}
            title={t('font_size')}
          >
            <option value="small">A−</option>
            <option value="medium">A</option>
            <option value="large">A+</option>
          </select>

          <div className={styles.legalLinks}>
            <Link to={`/impressum`} className={styles.legalLink}>
              {t('impressum')}
            </Link>
            <Link to={`/privacy`} className={styles.legalLink}>
              {t('privacy')}
            </Link>
          </div>

          {!isLoading && (
            user ? (
              <div className={styles.userInfo}>
                <Link to="/settings" className={styles.profileLink} aria-label={t('account_settings')} title={t('account_settings')}>
                  👤
                </Link>
                <span className={styles.username}>{user.username}</span>
                <button
                  type="button"
                  className={styles.loginBtn}
                  onClick={() => void signOut()}
                >
                  {t('sign_out')}
                </button>
              </div>
            ) : (
              <div className={styles.guestActions}>
                <button
                  type="button"
                  className={styles.registerLink}
                  onClick={register}
                >
                  {t('register')}
                </button>
                <button
                  type="button"
                  className={styles.loginBtn}
                  onClick={signIn}
                >
                  {t('sign_in')}
                </button>
              </div>
            )
          )}
        </div>
      </nav>
    </header>
  )
}

